import { For, Show, createSignal, type Component } from 'solid-js';

import { invitePlayer, leaveParty, respondToInvite } from '../net/protocol';
import type { GameStore } from '../state/store';

/**
 * The social panel: who is online, and who you can ask to play.
 *
 * ── Search by name, because a name is what a player can see ─────────────────
 *
 * Ids appear on no screen anywhere. Inviting by name is not only friendlier — it is what
 * stops a client enumerating the server's roster by trying handles, which inviting by id
 * would allow.
 *
 * The online list is what the server sent, filtered here as you type. Filtering locally
 * rather than asking per keystroke: the list is names, it is small, and a request per
 * character would be a request per character.
 *
 * ── What is deliberately missing ────────────────────────────────────────────
 *
 * **Friends and guilds are not built.** The panel says so where they would be, rather than
 * showing empty sections that look like a bug or — worse — a list that never populates.
 * Both need a table, a management story and an invite flow of their own; the party half is
 * complete and stands on its own.
 */
export const SocialPanel: Component<{ store: GameStore }> = (props) => {
  const [search, setSearch] = createSignal('');

  const online = () => props.store.social().online ?? [];
  const matches = () => {
    const q = search().trim().toLowerCase();
    const names = online();
    return q === '' ? names : names.filter((n) => n.toLowerCase().includes(q));
  };

  const inParty = () => (props.store.party().members ?? []).length > 0;

  return (
    <Show when={props.store.menu() === 'social'}>
      <div class="sheet" data-testid="social-panel">
        <div class="plate plate--riveted sheet__panel hud-interactive">
          <header class="sheet__head">
            <h2 class="sheet__title">Social</h2>
            <button
              type="button"
              class="sheet__close"
              data-testid="social-close"
              onClick={() => props.store.closeMenu()}
              aria-label="Close"
            >
              ✕
            </button>
          </header>

          {/*
            The invite, first and unmissable: it is the one thing here that is *waiting on
            you*, and burying it under a list of names would let it expire unnoticed.
          */}
          <Show when={props.store.social().invite}>
            {(offer) => (
              <div class="social__invite" data-testid="social-invite" role="status">
                <p>
                  <strong>{offer().fromName}</strong> invited you to a party.
                </p>
                <div class="social__invite-actions">
                  <button
                    type="button"
                    class="sigil-button"
                    data-testid="invite-accept"
                    onClick={() => props.store.send(respondToInvite(true))}
                  >
                    <span>Accept</span>
                  </button>
                  <button
                    type="button"
                    class="skills__button"
                    data-testid="invite-decline"
                    onClick={() => props.store.send(respondToInvite(false))}
                  >
                    Decline
                  </button>
                </div>
              </div>
            )}
          </Show>

          <section class="social__section" aria-label="Online players">
            <h3 class="station__heading">
              Online · <span data-testid="social-count">{online().length}</span>
            </h3>

            <input
              type="search"
              class="vault__search"
              placeholder="Search a name…"
              aria-label="Search online players"
              data-testid="social-search"
              value={search()}
              onInput={(e) => setSearch(e.currentTarget.value)}
            />

            <Show
              when={matches().length > 0}
              fallback={
                <p class="sheet__seam" data-testid="social-empty">
                  {online().length === 0
                    ? 'Nobody else is online.'
                    : 'No one online matches that name.'}
                </p>
              }
            >
              <ul class="social__list" data-testid="social-list">
                <For each={matches()}>
                  {(name) => (
                    <li class="social__row" data-testid={`social-${name}`}>
                      <span class="social__name">{name}</span>
                      <button
                        type="button"
                        class="skills__button"
                        data-testid={`invite-${name}`}
                        onClick={() => props.store.send(invitePlayer(name))}
                      >
                        Invite
                      </button>
                    </li>
                  )}
                </For>
              </ul>
            </Show>
          </section>

          <Show when={inParty()}>
            <button
              type="button"
              class="skills__button"
              data-testid="social-leave-party"
              onClick={() => props.store.send(leaveParty())}
            >
              Leave party
            </button>
          </Show>

          {/*
            Named rather than left as empty sections. An empty *Friends* list is
            indistinguishable from one that failed to load, and the same panel showing a
            working party half and a silent guild half would read as the guild half being
            broken.
          */}
          <p class="sheet__seam" data-testid="social-unbuilt">
            Friends and guilds are not built yet. Parties are — invite somebody above, and
            use the <strong>Party</strong> channel in chat once you have one.
          </p>
        </div>
      </div>
    </Show>
  );
};
